import { convertKeysToCamelCase, formatSeconds, formatTime, getUuid } from '@/sub-luca-plugin/utils';
import { IQaRecordItem } from '@/sub-luca-plugin/components/qa-render/qa-records';
import { IAskChatRecord, IGuessYouAskParams, IQaPairDetail, TRecordType } from '@/sub-luca-plugin/types';
import { eventBusExample } from './class-example';
import { RcFile } from 'antd/es/upload';

// 生成唯一的聊天记录的id
export const createQaRecordId = (role: 'questioner' | 'respondent' | 'system') => {
    return `${role}_record_key_${getUuid()}`;
};

interface IOptions {
    /**
     * 第一条记录是否展示日期
     */
    showDateByFirstRecord?: boolean;
    /**
     * 是否已解决卡片的title
     */
    whetherSolveTitle?: string;
    /**
     * 创建卡片的参数recordType类型
     */
    createRecordType?: TRecordType;
}
/**
 * 转换新的答疑记录
 */
export const transformToQaRecords = (list: IAskChatRecord[], options: IOptions = {}) => {
    const { showDateByFirstRecord, whetherSolveTitle, createRecordType } = options;
    let isLastQaAgain: boolean | undefined;
    let newestLucaAskChatId: string | undefined;
    let guessYouAskParams: { recordType: 50; guessYouQuestion: IGuessYouAskParams } | undefined;
    const newList = list || [];
    const lastAiRecordIndex = newList.length - [...newList].reverse().findIndex(item => item.recordType === 15) - 1;
    const records = newList.reduce((total, item, index) => {
        const { recordType, recordId, lucaAskChatId, createdAt, content, lastAskAgain } = item;
        if (!newestLucaAskChatId) newestLucaAskChatId = lucaAskChatId;
        if (!isLastQaAgain) isLastQaAgain = !!lastAskAgain;
        switch (recordType) {
            case 10:
            case 22:
                total.push({
                    recordId,
                    role: 'questioner',
                    type: 'basic',
                    content,
                    pictures: item.images || [],
                    createdAt: showDateByFirstRecord && index === 0 ? item.createdAt.slice(0, 16) : undefined,
                });
                break;
            case 14:
            case 25:
                total.push({ recordId, role: 'respondent', type: 'teacherReply', content: item.content });
                break;
            case 15:
                if (!item.content && !item.reasoningContent) return total;
                total.push({
                    recordId,
                    role: 'respondent',
                    type: 'ai',
                    content: item.content,
                    feedbackType: item.feedbackType ? item.feedbackType : undefined,
                    isFeedbackEvaluation: item.feedbackState === 1,
                    permanentActionBar: index === lastAiRecordIndex,
                    reasoningContent: item.reasoningContent,
                    reasoningContentState: item.reasoningContentState,
                });
                break;
            case 16:
                if (createRecordType === 46) {
                    guessYouAskParams = { recordType: 50, guessYouQuestion: { scene: 3 } };
                }
                total.push({ recordId, role: 'respondent', type: 'streamTips', content });
                break;
            case 17:
                total.push({ recordId, role: 'system', type: 'whetherSolve', state: 2, title: whetherSolveTitle });
                break;
            case 18:
                total.push({ recordId, role: 'system', type: 'whetherSolve', state: 3, title: whetherSolveTitle });
                break;
            case 19:
                total.push({ recordId, role: 'system', type: 'teacherQaTips', date: createdAt.slice(0, 16) });
                break;
            case 20:
            case 30:
                {
                    const info = item.voiceResponseCard;
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'voiceReply',
                        params: {
                            audioKey: `audio_key_${getUuid()}`,
                            linkContent: info.voiceContent,
                            linkFile: info.voiceUrl,
                            linkTime: info.times,
                        },
                    });
                }
                break;
            case 21:
            case 35:
                {
                    const dataSource: any = item?.similarProblems || item.helpYouCard?.similarProblems || {};
                    const questions = dataSource.items || [];
                    if (questions.length) {
                        total.push({
                            recordId,
                            role: 'respondent',
                            type: 'similarQuestion',
                            dataSource: JSON.parse(JSON.stringify(dataSource)),
                            list: questions.map(item => {
                                return {
                                    askId: item.askId,
                                    ask: item.ask
                                        .replace(/<em>/g, '<span class="similar-search-drawer__question-describe--mark">')
                                        .replace(/<\/em>/g, '</span>'),
                                };
                            }),
                        });
                    }
                }
                break;
            case 40:
                {
                    const ocrContent = item.askOcrContent?.ocrContent || '';
                    guessYouAskParams = { recordType: 50, guessYouQuestion: { scene: 0, question: ocrContent, saveFlag: false } };
                    total.push({
                        recordId,
                        role: 'questioner',
                        type: 'ocr',
                        content: ocrContent,
                        picture: item.images?.[0],
                        hideOcrContentEditBtn: !item.askOcrContent?.editState,
                    });
                }
                break;
            case 45:
                {
                    const picAnswer = item.picAnswer;
                    const searchContent = (picAnswer?.selectList || []).reduce((richText, item) => {
                        return richText + `<br>${item.option}、${item.content}`;
                    }, (picAnswer?.title || '') + (picAnswer?.littleTitle ? `<br>${picAnswer!.littleTitle}` : ''));
                    const questionId = picAnswer?.questionId || 0;
                    const littleQuestionId = picAnswer?.littleQuestionId;
                    guessYouAskParams = {
                        recordType: 50,
                        guessYouQuestion: { scene: 0, question: searchContent, questionId, littleQuestionId },
                    };
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'questionOfOcrSearch',
                        similarity: picAnswer?.similarity || 0,
                        content: searchContent,
                        questionId,
                        littleQuestionId,
                        hideReplaceOcrResultBtn: !picAnswer?.showButton,
                        everyAsks: picAnswer?.everyAsks || [],
                        cardSource: picAnswer?.cardSource || 0,
                    });
                }
                break;
            case 50:
                {
                    const questions = Object.values(item.guessYouQuestion?.guessYouQuestions || {});
                    if (questions.length) {
                        total.push({
                            recordId,
                            role: 'respondent',
                            type: 'guessYouAsk',
                            questions,
                            relateId: item.guessYouQuestion?.relateId || 0,
                        });
                    }
                }
                break;
            case 60:
                total.push({ recordId, role: 'respondent', type: 'rechargeLucaQa' });
                break;
            case 61:
                total.push({ recordId, role: 'respondent', type: 'rechargeTeacherQa' });
                break;
            case 65:
                {
                    const info = item.subjectSearch;
                    const qaList = info?.qaList || [];
                    const knowledgeSectionList = info?.knowledgeSectionList || [];
                    total.push({
                        recordId,
                        role: 'respondent',
                        type: 'subjectSearch',
                        dataSource: info && JSON.parse(JSON.stringify(info)),
                        sensorsData: {
                            lucaAskChatId,
                            recordId,
                            qaNum: qaList.length,
                            videoNum: knowledgeSectionList.filter(item => item.resourceType === 0).length,
                            knowledgePointsNum: knowledgeSectionList.filter(item => item.resourceType === 1).length,
                        },
                        qaList,
                        knowledgeSectionList,
                    });
                }
                break;
            default:
                break;
        }
        return total;
    }, [] as IQaRecordItem[]);
    return {
        success: true,
        newestLucaAskChatId,
        isLastQaAgain,
        records,
        guessYouAskParams,
    };
};

const getTypeAndIdBySource = (source: any): { sourceType?: 'video' | 'question'; id?: string; url?: string } => {
    switch (source.type) {
        case 'video':
            return { sourceType: 'video', id: source.sewise?.sourceId };
        case 'live_new':
            // 精简回放
            if (source.resourceType === 1) {
                return { sourceType: 'video', id: source?.live?.simplifyRecordUrl };
            }
            // 实时回放
            if (source.resourceType === 5) {
                return { sourceType: 'video', url: source?.live?.urlLivePlayBackPc };
            }
            return {};
        case 'smart_school':
            return { sourceType: 'video', id: source?.playbackLink };
        case 'item':
            return { sourceType: 'question', id: source.itemId };
        default:
            return {};
    }
};

/**
 * 根据问题来源数据转换为问题来源记录
 */
export const transformToQuestionSourceRecordBySource = (data: any): IQaRecordItem | null => {
    const source = convertKeysToCamelCase(data);
    // console.log('source', source);
    const { sourceType, id, url } = getTypeAndIdBySource(source);
    if (!sourceType) return null;
    return {
        recordId: createQaRecordId('respondent'),
        role: 'respondent',
        type: 'questionSource',
        sourceType,
        title: (source.sourceName || '').split(' - ').reverse()[0] || '',
        content: `${source.sourceName}${sourceType === 'video' ? '-' + formatSeconds(source.videoTime) : ''}`,
        questionId: id,
        videoId: id,
        url,
        videoTime: source.videoTime,
        permission: source.permission,
    };
};

/**
 * 转换老的答疑记录
 */
export const transformToQaRecordsByOldQaDetail = ({ answer, source, permission }: any) => {
    const records: IQaRecordItem[] = [];
    const questionSourceRecord = transformToQuestionSourceRecordBySource({ ...(source || {}), permission });
    if (questionSourceRecord) {
        records.push(questionSourceRecord);
    }
    return (answer || []).reduce((total: IQaRecordItem[], item: any, index: number) => {
        if (item.question_content) {
            total.push({
                recordId: createQaRecordId('questioner'),
                role: 'questioner',
                type: 'basic',
                content: item.question_content,
                pictures: item.images ? JSON.parse(item.images) : [],
            });
        }
        if (index === 0) {
            total.push({
                recordId: createQaRecordId('system'),
                role: 'system',
                type: 'teacherQaTips',
                date: formatTime(item.ask_time * 1000, 'YYYY-MM-DD HH:mm'),
            });
        }
        if (item.answer_content) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'teacherReply',
                content: item.answer_content,
            });
        }
        if (item.voice_list?.length) {
            item.voice_list.reduce((total: IQaRecordItem[], voiceItem: any) => {
                total.push({
                    recordId: createQaRecordId('respondent'),
                    role: 'respondent',
                    type: 'voiceReply',
                    params: {
                        audioKey: `audio_key_${getUuid()}`,
                        linkFile: voiceItem.file_link,
                        linkContent: voiceItem.link_content,
                        linkTime: +voiceItem.link_time,
                    },
                });
                return total;
            }, total);
        }
        if (item.more_answer_content) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'teacherReply',
                content: item.more_answer_content,
            });
        }
        if (item.more_file_link) {
            total.push({
                recordId: createQaRecordId('respondent'),
                role: 'respondent',
                type: 'voiceReply',
                params: {
                    audioKey: `audio_key_${getUuid()}`,
                    linkContent: item.more_link_content,
                    linkFile: item.more_file_link,
                    linkTime: +item.more_link_time,
                },
            });
        }
        return total;
    }, records);
};

/**
 * 转换Qa对答疑记录
 */
export const transformToQaRecordsByPairOfQaDetail = ({ qaRecords, source, permission, qaId: flashQaId }: IQaPairDetail) => {
    const records: IQaRecordItem[] = [];
    const questionSourceRecord = transformToQuestionSourceRecordBySource({ ...(source || {}), permission });
    if (questionSourceRecord) {
        records.push(questionSourceRecord);
    }
    let flashIndex = 0;
    const newQaRecords = qaRecords || [];
    const list = newQaRecords.reduce((total: IQaRecordItem[], item, index) => {
        let isFlash = false;
        if (qaRecords.length > 1 && item.qaId === flashQaId) {
            flashIndex = index;
            isFlash = true;
        }
        total.push(
            {
                recordId: `questioner_${item.qaId}`,
                role: 'questioner',
                type: 'qaPairQuestioner',
                content: item.question || '',
                isFlash,
            },
            {
                recordId: `respondent_${item.qaId}`,
                role: 'respondent',
                type: 'qaPairReply',
                content: item.answer || '',
                isFlash,
            }
        );
        return total;
    }, records);
    return { flashIndex, records: list };
};

export const toastInQa = (message: string, iconType: 'warning' | 'success' = 'warning') => {
    eventBusExample.publish('QaToast', { type: 'show', params: { message, iconType } });
};

export const toastInQaDetail = (message: string, iconType: 'warning' | 'success' = 'warning') => {
    eventBusExample.publish('QaDetailToast', { type: 'show', params: { message, iconType } });
};

export const uploadLimit = (file: RcFile) => {
    if (!['image/png', 'image/jpg', 'image/jpeg'].includes(file.type)) {
        toastInQa('仅支持上传png/jpg/jpeg格式的图片');
        return false;
    }
    if (file.size > 10 * 1024 * 1024) {
        toastInQa('图片大小不能超过10M~');
        return false;
    }
    return true;
};
